// Admin Backend Services Page - Track API usage and limits
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Layout from '@/components/Layout';
import { AlertTriangle, CheckCircle, DollarSign, Zap, Database, Cloud, RefreshCw } from 'lucide-react';

interface ServiceStatus {
  name: string;
  provider: string;
  status: 'active' | 'warning' | 'error';
  usage: {
    current: number;
    limit: number;
    unit: string;
  };
  cost: {
    current: number;
    estimated: number;
  };
  lastChecked: string;
}

export default function AdminBackendServices() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is admin
    if (!user || user.role !== 'admin') {
      navigate('/organizer');
      return;
    }

    loadServiceStatus();
  }, [user, navigate]);

  const loadServiceStatus = async () => {
    setIsLoading(true);
    
    // Mock data - you can replace with real API calls
    const mockServices: ServiceStatus[] = [
      {
        name: 'OpenAI Whisper',
        provider: 'OpenAI',
        status: 'active',
        usage: {
          current: 450,
          limit: 1000,
          unit: 'minutes/month'
        },
        cost: {
          current: 2.70,
          estimated: 6.00
        },
        lastChecked: new Date().toISOString()
      },
      {
        name: 'OpenAI GPT-3.5 Turbo',
        provider: 'OpenAI',
        status: 'active',
        usage: {
          current: 8500,
          limit: 10000,
          unit: 'tokens/min'
        },
        cost: {
          current: 45.20,
          estimated: 60.00
        },
        lastChecked: new Date().toISOString()
      },
      {
        name: 'Cloudflare Pages',
        provider: 'Cloudflare',
        status: 'active',
        usage: {
          current: 15000,
          limit: 500000,
          unit: 'requests/day'
        },
        cost: {
          current: 0,
          estimated: 0
        },
        lastChecked: new Date().toISOString()
      },
      {
        name: 'Turso Database',
        provider: 'Turso',
        status: 'active',
        usage: {
          current: 2.5,
          limit: 9,
          unit: 'GB storage'
        },
        cost: {
          current: 0,
          estimated: 0
        },
        lastChecked: new Date().toISOString()
      },
      {
        name: 'Supabase Realtime',
        provider: 'Supabase',
        status: 'active',
        usage: {
          current: 150000,
          limit: 500000,
          unit: 'messages/month'
        },
        cost: {
          current: 0,
          estimated: 0
        },
        lastChecked: new Date().toISOString()
      }
    ];

    setServices(mockServices);
    setIsLoading(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-400 bg-green-500/10 border-green-500/30';
      case 'warning': return 'text-amber-400 bg-amber-500/10 border-amber-500/30';
      case 'error': return 'text-red-400 bg-red-500/10 border-red-500/30';
      default: return 'text-slate-400 bg-slate-500/10 border-slate-500/30';
    }
  };

  const getUsagePercentage = (current: number, limit: number) => {
    return (current / limit) * 100;
  };

  const getUsageStatus = (percentage: number): 'active' | 'warning' | 'error' => {
    if (percentage >= 90) return 'error';
    if (percentage >= 75) return 'warning';
    return 'active';
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-b from-[#05020b] via-[#0a0513] to-[#0f0820] flex items-center justify-center">
          <div className="text-center">
            <RefreshCw className="w-12 h-12 text-purple-400 animate-spin mx-auto mb-4" />
            <p className="text-slate-400">Loading backend services...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-[#05020b] via-[#0a0513] to-[#0f0820] p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Backend Services & Infrastructure</h1>
            <p className="text-slate-400">Monitor API usage, costs, and service health</p>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-gradient-to-br from-purple-900/40 to-purple-800/20 border border-purple-500/30 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <Zap className="w-6 h-6 text-purple-400" />
                <h3 className="text-lg font-semibold text-white">Active Services</h3>
              </div>
              <p className="text-3xl font-bold text-purple-300">{services.length}</p>
            </div>

            <div className="bg-gradient-to-br from-amber-900/40 to-amber-800/20 border border-amber-500/30 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-6 h-6 text-amber-400" />
                <h3 className="text-lg font-semibold text-white">Current Month Cost</h3>
              </div>
              <p className="text-3xl font-bold text-amber-300">
                ${services.reduce((sum, s) => sum + s.cost.current, 0).toFixed(2)}
              </p>
              <p className="text-xs text-amber-400/60 mt-1">
                Est. ${services.reduce((sum, s) => sum + s.cost.estimated, 0).toFixed(2)} at end of month
              </p>
            </div>

            <div className="bg-gradient-to-br from-red-900/40 to-red-800/20 border border-red-500/30 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <AlertTriangle className="w-6 h-6 text-red-400" />
                <h3 className="text-lg font-semibold text-white">Alerts</h3>
              </div>
              <p className="text-3xl font-bold text-red-300">
                {services.filter(s => getUsageStatus(getUsagePercentage(s.usage.current, s.usage.limit)) !== 'active').length}
              </p>
            </div>
          </div>

          {/* Services List */}
          <div className="space-y-4">
            {services.map((service, idx) => {
              const usagePercent = getUsagePercentage(service.usage.current, service.usage.limit);
              const usageStatus = getUsageStatus(usagePercent);
              
              return (
                <div
                  key={idx}
                  className="bg-slate-900/50 border border-slate-700/50 rounded-xl p-6 hover:border-purple-500/30 transition-colors"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-white">{service.name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(usageStatus)}`}>
                          {usageStatus === 'active' && <CheckCircle className="inline w-3 h-3 mr-1" />}
                          {usageStatus === 'warning' && <AlertTriangle className="inline w-3 h-3 mr-1" />}
                          {usageStatus === 'error' && <AlertTriangle className="inline w-3 h-3 mr-1" />}
                          {usageStatus === 'active' ? 'Healthy' : usageStatus === 'warning' ? 'High Usage' : 'Critical'}
                        </span>
                      </div>
                      <p className="text-sm text-slate-400">Provider: {service.provider}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-purple-300">${service.cost.current.toFixed(2)}</p>
                      <p className="text-xs text-slate-400">this month</p>
                    </div>
                  </div>

                  {/* Usage Bar */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-300">Usage</span>
                      <span className="text-sm font-semibold text-white">
                        {service.usage.current.toLocaleString()} / {service.usage.limit.toLocaleString()} {service.usage.unit}
                      </span>
                    </div>
                    <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          usageStatus === 'error' ? 'bg-gradient-to-r from-red-500 to-red-600' :
                          usageStatus === 'warning' ? 'bg-gradient-to-r from-amber-500 to-amber-600' :
                          'bg-gradient-to-r from-green-500 to-green-600'
                        }`}
                        style={{ width: `${Math.min(usagePercent, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">{usagePercent.toFixed(1)}% used</p>
                  </div>

                  {/* Alerts */}
                  {usageStatus !== 'active' && (
                    <div className={`rounded-lg p-3 flex items-start gap-2 ${
                      usageStatus === 'error' ? 'bg-red-500/10 border border-red-500/30' :
                      'bg-amber-500/10 border border-amber-500/30'
                    }`}>
                      <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${
                        usageStatus === 'error' ? 'text-red-400' : 'text-amber-400'
                      }`} />
                      <div>
                        <p className={`text-sm font-semibold ${
                          usageStatus === 'error' ? 'text-red-300' : 'text-amber-300'
                        }`}>
                          {usageStatus === 'error' ? '⚠️ Critical Usage Level' : '⚠️ High Usage Warning'}
                        </p>
                        <p className={`text-xs ${
                          usageStatus === 'error' ? 'text-red-400/80' : 'text-amber-400/80'
                        }`}>
                          {usageStatus === 'error' 
                            ? 'You are approaching your limit. Service may be interrupted if limit is reached.'
                            : 'Consider upgrading or monitoring usage closely to avoid service interruption.'}
                        </p>
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-slate-500 mt-3">
                    Last checked: {new Date(service.lastChecked).toLocaleString()}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Refresh Button */}
          <div className="mt-8 flex justify-center">
            <button
              onClick={loadServiceStatus}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold rounded-xl flex items-center gap-2 shadow-lg"
            >
              <RefreshCw className="w-5 h-5" />
              Refresh Status
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
